import React from "react";
import moment from "moment";
import { useRef, useState } from "react";
import { SingleDatePicker } from "react-dates";
import "react-dates/lib/css/_datepicker.css";
import "react-dates/initialize";
import "./input.scss";
import { AutoFillTypes } from "src/api/types";
import FormGroup from "src/components/form/FormGroup";
import AutoFillOptions from "src/components/form/Input/AutoFillOptions";
import Skeleton from "src/components/Skeleton";
import { useTranslation } from "react-i18next";

interface InputProps {
	type?: string;
	placeholder?: string;
	label?: string;
	value?: any;
	name: string;
	onChange?: (key: string, value: any) => void;
	handleChange?: (e: any) => void;
	handleBlur?: (e: any) => void;
	onFocus?: (e: any) => void;
	onKeyDown?: (e: any) => void;
	onKeyPress?: (e: any) => void;
	error?: any | boolean;
	valid?: string | boolean | number;
	loading?: boolean;
	multiline?: boolean;
	optional?: boolean;
	showPast?: boolean;
	button?: any;
	help?: string;
	autoFill?: AutoFillTypes;
	autoFillQuery?: string;
	autoFocus?: boolean;
	increments?: boolean;
	variant?: "dark";
	className?: string;
	icon?: any;
	noMarginBottom?: boolean;
	disabled?: boolean;
	inputId?: string;
	withTime?: boolean;
	textareaClassName?: string;
	layout: "stacked" | "horizontal";
	step?: any;
}

const Input = ({
	type,
	value,
	placeholder,
	label,
	onChange,
	name,
	handleChange,
	handleBlur,
	onFocus,
	onKeyDown,
	onKeyPress,
	error,
	valid,
	loading,
	multiline,
	showPast,
	button,
	help,
	autoFill,
	autoFillQuery,
	autoFocus,
	increments,
	variant,
	optional,
	className,
	icon,
	noMarginBottom,
	disabled,
	inputId,
	withTime,
	textareaClassName,
	layout,
	step,
}: InputProps) => {
	const { t } = useTranslation("form", {
		keyPrefix: "input",
	});
	const ref = useRef(null);
	const input = useRef(null) as any;
	const classes = ["input"];
	const [focused, onFocusChange] = useState(false) as any;
	if (error) classes.push("input_has-error");
	if (valid && !error) classes.push("input_is-valid");
	if (type) classes.push(`input_${type}`);
	if (variant) classes.push(`input_${variant}`);
	if (disabled) classes.push(`input_disabled`);
	if (increments) classes.push(`input_increments`);
	if (button) classes.push("input-has-button");
	if (autoFill) classes.push("input-has-auto-fill");
	if (className) classes.push(className);
	if (icon || type === "telephone") {
		classes.push("input-has-prefix");
	}
	let time = null;

	if (type === "date" && withTime) {
		time = value ? moment(value).format("HH:mm") : undefined;
	}

	if (type === "date" && typeof value === "string") {
		value = moment(value, "YYYY-MM-DD");
	}

	if (type === "date" && value && value?.getTime && !isNaN(value.getTime())) {
		value = moment(value);
	}

	if (type === "telephone" && value && value.includes("+31")) {
		value = value.replace("+31", "");
	}

	const getYears = () => {
		let years = [];
		for (
			let i = moment().year() - (showPast ? 100 : 0);
			i <= moment().year() + 4;
			i++
		) {
			years.push(i);
		}
		return years;
	};

	const renderMonthElement = ({ month, onYearSelect }: any) => {
		return (
			<div className="month-element">
				<div className="month-element-head">
					<h2>{month.format("MMMM")}</h2>
				</div>

				<div>
					<select
						defaultValue={month.year()}
						onChange={(e) => onYearSelect(month, e.target.value)}
					>
						{getYears().map((i) => (
							<option key={`${name}-year-${i}`} value={i}>
								{i}
							</option>
						))}
					</select>
				</div>
			</div>
		);
	};

	const handleIncrements = (type: "min" | "plus") => {
		value = Number(value);
		if (type === "min" && value > 1) {
			value = value - 1;
		} else if (type === "plus") {
			value = value + 1;
		}
		if (onChange) onChange(name, value);
		if (handleChange)
			handleChange({
				target: {
					name,
					value,
				},
			});
	};

	if (type === "date") {
		return (
			<>
				<FormGroup
					{...{
						ref,
						label,
						valid,
						help,
						error,
						type,
						loading,
						disabled,
						noMarginBottom,
						optional,
						layout,
					}}
					className={classes.join(" ")}
				>
					<div className="flex flex-col flex-1 gap-2">
						<div className="flex flex-1 gap-2">
							<div className="flex-1">
								<SingleDatePicker
									isOutsideRange={
										showPast ? () => false : undefined
									}
									showDefaultInputIcon={true}
									placeholder={placeholder || label || ""}
									numberOfMonths={1}
									date={value || null}
									enableOutsideDays={true}
									readOnly
									onDateChange={(date: any) => {
										let value = null;
										if (moment(date)) {
											let asDate = moment(
												date
											).toDate() as any;
											if (!isNaN(asDate.getTime())) {
												value = asDate;
											}
										}
										if (onChange) onChange(name, value);
										if (handleChange)
											handleChange({
												target: {
													name,
													value,
												},
											});
									}}
									onFocusChange={({ focused }) =>
										onFocusChange(focused)
									}
									{...{ focused, renderMonthElement }}
									id={name} // PropTypes.string.isRequired,
								/>
							</div>
							{withTime && (
								<input
									type="time"
									className="max-w-[120px] w-full"
									value={time || ""}
									placeholder="Tijd"
									onChange={(e: any) => {
										if (disabled) return;
										let date = value
											? moment(value)
											: moment();
										let _value = e.target.value;

										const [hours, minutes] =
											_value.split(":");
										date.set({
											hours,
											minutes,
										});
										if (onChange) {
											onChange(name, date.toDate());
										}
										if (handleChange)
											handleChange({
												target: {
													name,
													value: date.toDate(),
												},
											});
									}}
								/>
							)}
						</div>
						{optional && (
							<small
								onClick={() => {
									const value = null;
									if (onChange) onChange(name, value);
									if (handleChange)
										handleChange({
											target: {
												name,
												value,
											},
										});
								}}
								className="cursor-pointer transition-all hover:opacity-90"
							>
								{t("clear")}
							</small>
						)}
					</div>
				</FormGroup>
			</>
		);
	}

	const handleFocusInput = () => {
		if (input?.current) {
			input.current?.focus();
		}
	};

	const renderInput = () => (
		<input
			id={inputId}
			onChange={(e: any) => {
				if (disabled) return;
				if (type === "number") {
					const str = e.target.value;
					let transformed = str
						? str.toString().replace(/B(?=(d{3})+(?!d))/g, ",")
						: str;
					if (transformed) e.target.value = transformed;
				}
				if (onChange && type === "price") {
					onChange(name, parseFloat(e.target.value));
				}
				if (onChange && type !== "price")
					onChange(name, e.target.value);
				if (handleChange) handleChange(e);
			}}
			onBlur={handleBlur}
			placeholder={placeholder || label}
			{...{ name, onFocus, onKeyDown, onKeyPress, autoFocus, step }}
			ref={input}
			type={type === "price" ? "number" : type}
			{...(type === "time"
				? {
						pattern: "[0-2][0-9]:[0-5][0-9]",
						step: 15 * 60,
						defaultValue: value || "",
				  }
				: { value })}
		/>
	);

	return (
		<FormGroup
			{...{
				ref,
				label,
				valid,
				help,
				error,
				type,
				loading,
				name,
				optional,
				noMarginBottom,
				disabled,
				layout,
			}}
			className={classes.join(" ")}
		>
			{icon && (
				<div
					onClick={handleFocusInput}
					className="telephone-prefix -mr-2"
				>
					{icon}
				</div>
			)}
			{type === "telephone" && (
				<div onClick={handleFocusInput} className="telephone-prefix">
					<p>+31</p>
				</div>
			)}
			{type === "price" && (
				<div onClick={handleFocusInput} className="price-prefix">
					<p>&euro;</p>
				</div>
			)}

			{increments && type === "number" && !disabled && (
				<div
					onClick={() => handleIncrements("min")}
					className="input-increment"
				>
					<span>
						<i className="fal fa-minus"></i>
					</span>
				</div>
			)}
			{multiline ? (
				<textarea
					id={inputId}
					rows={2}
					onChange={(e) => {
						if (disabled) return;
						if (onChange) onChange(name, e.target.value);
						if (handleChange) handleChange(e);
					}}
					onBlur={handleBlur}
					placeholder={placeholder || label}
					ref={input}
					className={textareaClassName}
					{...{ value, name, type, onFocus, onKeyPress }}
				/>
			) : button ? (
				<div className="input-row">
					{renderInput()}
					{button}
				</div>
			) : (
				renderInput()
			)}
			{increments && type === "number" && !disabled && (
				<div
					onClick={() => handleIncrements("plus")}
					className="input-increment"
				>
					<span>
						<i className="fal fa-plus"></i>
					</span>
				</div>
			)}

			{autoFill && (
				<AutoFillOptions
					{...{ value }}
					query={autoFillQuery || ""}
					onSelect={(s) => {
						if (onChange) {
							onChange(name, s);
						}
						if (handleChange) {
							handleChange({
								target: {
									name,
									value: s,
								},
							});
						}
					}}
					type={autoFill}
				/>
			)}
		</FormGroup>
	);
};

const Loading = () => {
	return (
		<FormGroup.Loading>
			<Skeleton className="w-full h-[50px]" />
		</FormGroup.Loading>
	);
};

Input.Loading = Loading;

Input.defaultProps = {
	layout: "stacked",
};

export default Input;
